import { Component, HostListener, Inject, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { TenantContextService } from '../../services/tenant-context.service';
import { WorkspaceService } from '../../tabs/workspaces-tab/services/workspace.service';
import { Workspace } from '../../tabs/workspaces-tab/models/workspace.model';
import { WorkspacePublicNetworkAccessType } from '../../tabs/workspaces-tab/models/workspace-public-network-access.model';
import { catchError, concatMap, EMPTY, forkJoin, from, Observable, of, Subject, Subscription, tap, toArray } from 'rxjs';

@Component({
  selector: 'app-edit-workspace-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    LoadingSpinnerComponent
],
  templateUrl: './edit-workspace-dialog.component.html',
  styleUrl: './edit-workspace-dialog.component.css'
})
export class EditWorkspaceDialogComponent implements OnInit, OnDestroy {
  static closeDialog$ = new Subject<{ workspaceName: string, tenantId: string }>();
  private closeSub?: Subscription;

  tenantContextService = inject(TenantContextService);
  workspaceService = inject(WorkspaceService);

  workspace: Workspace;

  editWorkspaceForm: FormGroup;
  isSubmitting = signal<boolean>(false);
  submissionErrorMessage = signal<string>("");

  // network access enum for the template
  networkAccess = WorkspacePublicNetworkAccessType;

  constructor (
    private dialogRef: MatDialogRef<EditWorkspaceDialogComponent>, 
    private fb: FormBuilder, 
    @Inject(MAT_DIALOG_DATA) public data: Workspace
  ) {
    this.workspace = data;
    this.editWorkspaceForm = this.fb.group(
      {
        privateAccessToWorkspace: this.fb.control(
          this.workspace.publicNetworkAccess === this.networkAccess.Disabled
        ),
        workspaceAlias: [this.workspace.workspaceAlias || null, [
          // regex pattern validation
          // 1. Cannot have leading or trailing spaces
          // 2. 3-128 chars or empty
          // 3. Not all spaces (includes all white space characters)
          Validators.pattern(/^$|^(?! )(?!.* $)(?!\s+$).{3,128}$/)
        ]]
      },
      { validators: [this.atLeastOneChangedValidator()] }
    );

    if (this.workspace.publicNetworkAccess === this.networkAccess.Disabled) {
      this.editWorkspaceForm.get('privateAccessToWorkspace')?.disable();
    }
  }

  ngOnInit() {
    // create a subject subscription to be able to be called from any service to close an exact form
    this.closeSub = EditWorkspaceDialogComponent.closeDialog$.subscribe(({ workspaceName, tenantId }) => {
      if (this.workspace.workspaceName === workspaceName && this.tenantContextService.tenant()?.tenantId === tenantId) {
        this.dialogRef.close();
      }
    });
  }

  // validator to ensure that atleast one value has been changed within the form inorder for the form to be valid
  private atLeastOneChangedValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      // downcast to FormGroup to access controls
      const formGroup = control as FormGroup;
      const initialAlias = this.workspace.workspaceAlias;
      const initialPrivateAccess = this.workspace.publicNetworkAccess === this.networkAccess.Disabled;

      let alias = formGroup.get('workspaceAlias')?.value;
      if (alias === "") {
        alias = null;
      }

      const privateAccess = formGroup.get('privateAccessToWorkspace')?.value;

      const aliasChanged = alias !== initialAlias;
      const privateAccessChanged = privateAccess !== initialPrivateAccess;

      return (aliasChanged || privateAccessChanged) ? null : { nothingChanged: true };
    };
  }

  onSubmit(): void { 
    this.submissionErrorMessage.set("");
    if (!this.editWorkspaceForm.valid) return;

    const tenantId = this.tenantContextService.tenant()?.tenantId;
    if (!tenantId) return;

    const initialWorkspaceAlias: string | null = this.workspace.workspaceAlias;
    const initialPrivateAccess: boolean = this.workspace.publicNetworkAccess === this.networkAccess.Disabled;

    const newPrivateAccess: boolean = this.editWorkspaceForm.get('privateAccessToWorkspace')?.value;
    let newWorkspaceAlias: string | null = this.editWorkspaceForm.get('workspaceAlias')?.value;

    if (newWorkspaceAlias === "") {
      newWorkspaceAlias = null;
    }

    // guard to prevent changing private access (true) to public access (false)
    if (initialPrivateAccess && !newPrivateAccess) return;

    const hasWorkspaceAliasChanged: boolean = newWorkspaceAlias !== initialWorkspaceAlias;
    const hasPrivateAccessChanged: boolean = newPrivateAccess !== initialPrivateAccess;

    // at least one value needs to have been changed
    if (!hasWorkspaceAliasChanged && !hasPrivateAccessChanged) return;

    this.isSubmitting.set(true);

    // run all requests for the values that have changed sequentially
    let request$: Observable<any> = of(null);

    // if request succeeds update the workspace with the alias
    // if the request fails and further requests will not be made and an error message will be shown in the form
    if (hasWorkspaceAliasChanged) {
      request$ = request$.pipe(
        concatMap(() => 
          this.workspaceService.updateWorkspaceAlias({
            tenantId,
            workspaceId: this.workspace.workspaceId,
            newWorkspaceAlias
          }).pipe(
            tap((aliasResult) => {
              this.workspace.workspaceAlias = aliasResult;
            }),
            catchError((error) => {
              this.isSubmitting.set(false);
              this.submissionErrorMessage.set(error);
              return EMPTY;
            })
          )
        )
      );
    }

    // if request succeeds then will wait for SignalR notification
    // if request fails then form will remain open with an error message displayed but any predecessor call will still update the workspace
    if (hasPrivateAccessChanged) {
      request$ = request$.pipe(
        concatMap(() =>
          this.workspaceService.disableWorkspacePublicNetwork({
            tenantId,
            workspaceName: this.workspace.workspaceName
          }).pipe(
            catchError((error) => {
              this.isSubmitting.set(false);
              this.submissionErrorMessage.set(error);
              return EMPTY;
            })
          )
        )
      );
    }

    request$.subscribe({
      next: () => {
        if (hasPrivateAccessChanged) {
          // wait for SignalR
        } else {
          this.isSubmitting.set(false);
          this.dialogRef.close(this.workspace);
        }
      },
      error: (error) => {
        // fallback
        this.isSubmitting.set(false);
        this.submissionErrorMessage.set(error);
      }
    });
  }

  // display default browser popup on unloading page during form submission
  // does not warn navigating to another page of neuro utils as it is a SPA and would require a route guard for it
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isSubmitting()) {
      $event.preventDefault();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    // clean up the subject subscription used to close the form from a service
    this.closeSub?.unsubscribe();
  }
}
